
import { Observable, Observer, of } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import * as _ from 'lodash';
import * as $ from 'jquery';
import * as r from 'request';

import { PKConfig } from '../config';
import { Z_FILTERED } from 'zlib';

const pkConfig: PKConfig = require('../config.json');


/**
 * Services for query data from FuD API
 * 
 * @export
 * @class FudapiService
 * @author Sheng, Li 
 */
export class FudapiService {
  
  public static url: string = pkConfig.fudAPIHostProtocol + pkConfig.fudAPIHost + pkConfig.fudAPIRoot;

  public static headerOptions = {
    'Authorization': 'Bearer ' + pkConfig.fudAPIAT,
    'Accept-Encoding': 'gzip'
  };

  /**
   * get all data in a doc
   * 
   * @static
   * @param {number[]} docIds 
   * @returns {Observable<any>} 
   * @memberof FudapiService
   */
  public static getDocumentData(docIds: number[]): Observable<any> {
    const url: string = this.url + 'document/' + docIds.join(',');
    const obs = Observable.create((o: Observer<any>) => {
      r(
        {
          url: url,
          method: 'GET',
          headers: this.headerOptions
        },
        (err, res, body) => {
          if (err) {
            o.error(err);
          } else {
            let next = {};
            if (typeof body !== 'object') {
              next = JSON.parse(body);
            } else {
              next = body;
            }
            o.next(next);
          }
          o.complete();
        }
      );
    });
    return obs;
  }

  /**
   * get all data in a doc using filter
   * 
   * @static
   * @param {number[]} docIds 
   * @param {string} filter 
   * @returns {Observable<any>} 
   * @memberof FudapiService
   */
  public static getDocumentDataWithFilter(docIds: number[], filter: string): Observable<any> {
    
    const url: string = this.url + 'document/' + docIds.join(',');
    const obs = Observable.create((o: Observer<any>) => {
      r(
        {
          url: url,
          method: 'POST',
          headers: this.headerOptions,
          form: {
            filter: filter
          }
        },
        (err, res, body) => {
          if (err) {
            o.error(err);
          } else {
            let next = {};
            if (typeof body !== 'object') {
              next = JSON.parse(body);
            } else {
              next = body;
            }
            o.next(next);
          }
          o.complete();
        }
      );
    });
    return obs;
  }

  /**
   * get ids of linked documents according to given docId
   * 
   * @static
   * @param {number} docId 
   * @returns {Observable<any>} 
   * @memberof FudapiService
   */
  public static searchLinkedDocument(docId: number, docTypeId?: number, expandInfo?: 'true' | 'false'): Observable<any> {
    let url: string = this.url + 'searchLinkedDocument/' + docId;
    if (docTypeId || expandInfo) {
      url += '?';
      if (docTypeId) {
        url += 'docTypeId=' + docTypeId;
      }
      if (expandInfo) {
        url += '&expandInfo=' + expandInfo;
      }
    }
    const obs = Observable.create((o: Observer<any>) => {
      r(
        {
          url: url,
          method: 'GET',
          headers: this.headerOptions
        },
        (err, res, body) => {
          if (err) {
            o.error(err);
          } else {
            let next = {};
            if (typeof body !== 'object') {
              next = JSON.parse(body);
            } else {
              next = body;
            }
            o.next(next);
          }
          o.complete();
        }
      );
    });
    return obs;
  }

  /**
   * get ids of documents which habe the given type id
   * 
   * @static
   * @param {number} docTypeId 
   * @returns {Observable<any>} 
   * @memberof FudapiService
   */
  public static getDocumentIdsByType(docTypeId: number): Observable<any> {
    const url: string = this.url + 'documentids?docType=' + docTypeId;
    const obs = Observable.create((o: Observer<any>) => {
      r(
        {
          url: url,
          method: 'GET',
          headers: this.headerOptions
        },
        (err, res, body) => {
          if (err) {
            o.error(err);
          } else {
            let next = {};
            if (typeof body !== 'object') {
              next = JSON.parse(body);
            } else {
              next = body;
            }
            o.next(next);
          }
          o.complete();
        }
      );
    });
    return obs;
  }

  /**
   * search documents using given fields in body and given type id 
   * 
   * @static
   * @param {number} docTypeId 
   * @param {*} fields 
   * @param {('and' | 'or')} operator 
   * @returns {Observable<any>} 
   * @memberof FudapiService
   */
  public static searchDocumentByFields(docTypeId: number, fields: any, operator: 'and' | 'or'): Observable<any> {
    const url: string = this.url + 'searchDocumentByFields?searchDdocTypeId=' + docTypeId + '&operator=' + operator;
    const headerOptions: any = Object.assign({}, this.headerOptions);
    headerOptions['content-type'] = 'application/json';
    const obs = Observable.create((o: Observer<any>) => {
      r(
        {
          url: url,
          method: 'POST',
          headers: headerOptions,
          json: fields
        },
        (err, res, body) => {
          if (err) {
            o.error(err);
          } else {
            let next = {};
            if (typeof body !== 'object') {
              next = JSON.parse(body);
            } else {
              next = body;
            }
            o.next(next);
          }
          o.complete();
        }
      );
    });
    return obs;
  }
  
  /**
   * get URL of a file in FuD
   * 
   * @param {string} fileName 
   * @returns {Observable<any>} 
   * @memberof FudapiService
   */
  public static getFileURL(fileName: string): Observable<any> {
    const url: string = this.url + 'attachment/' + fileName;
    const headerOptions: any = Object.assign({}, this.headerOptions);
    const obs = Observable.create((o: Observer<any>) => {
      r(
        {
          url: url,
          method: 'GET',
          headers: headerOptions
        },
        (err, res, body) => {
          if (err) {
            o.error(err);
          } else {
            let next = {};
            if (typeof body !== 'object') {
              next = JSON.parse(body);
            } else {
              next = body;
            }
            o.next(next);
          }
          o.complete();
        }
      );
    });
    return obs;
  }
  
  /**
   * get the original name of a file in FuD
   * 
   * @param {string} fileName 
   * @returns {Observable<any>} 
   * @memberof FudapiService
   */
  public static getFileOriginalName(fileName: string): Observable<any> {
    const url: string = this.url + 'attachment/originalnames/';
    const headerOptions: any = Object.assign({}, this.headerOptions);
    headerOptions['content-type'] = 'application/json';
    const json = {};
    json['1'] = fileName;
    const obs = Observable.create((o: Observer<any>) => {
      r(
        {
          url: url,
          method: 'POST',
          headers: headerOptions,
          json: json
        },
        (err, res, body) => {
          if (err) {
            o.error(err);
          } else {
            let next = {};
            if (typeof body !== 'object') {
              next = JSON.parse(body);
            } else {
              next = body;
            }
            o.next(next);
          }
          o.complete();
        }
      );
    });
    return obs;
  }
  
  /**
   * get URL of a pic in FuD
   * 
   * @static
   * @param {string} sourceOriginalURL 
   * @param {number} timestamp 
   * @returns {Observable<any>} 
   * @memberof FudapiService
   */
  public static getDeepzoomURL(sourceOriginalURL: string, timestamp: number): Observable<any> {
    const url: string = pkConfig.osdHostProtocol + pkConfig.osdHost + pkConfig.osdRoot + 'utils/deepzoom/createDeepzoom.php';
    const headerOptions: any = {
      'Content-Type': 'application/x-www-form-urlencoded'
    };
    const obs = Observable.create((o: Observer<any>) => {
      r(
        {
          url: url,
          method: 'POST',
          headers: headerOptions,
          form: {
            source: encodeURI(sourceOriginalURL),
            timestamp: timestamp
          }
        },
        (err, res, body) => {
          if (err) {
            o.error(err);
          } else {
            let next = {};
            if (typeof body !== 'object') {
              next = JSON.parse(body);
            } else {
              next = body;
            }
            o.next(next);
          }
          o.complete();
        }
      );
    });
    return obs;
  }

  /**
   * transform freetext in some FuD fields for frontend.
   * 
   * Parameter 'obj' is used for changing str in async behavious.
   * 
   * @static
   * @param {{str: string}} obj 
   * @returns {{str: string}} 
   * @memberof FudapiService
   */
  public static transformFreetext(obj: {str: string}): Observable<any> {
    let res;
    let obs = of({});
    // if (obj.str) {
    //   const a = obj.str;
    //   console.log(a);
    // }

    // transform external link
    const elPattern = /<span class="id-(http|https)-\/\/(.*?)  ">(.*?)<\/span>/g;
    res = elPattern.exec(obj.str);
    while (res) {
      const foundStr = res[0];
      const protocol = res[1];
      const exLink = res[2];
      const content = res[3];
      const link = `<a target="_blank" href="${protocol}://${exLink}">${content}</a>`;
      obj.str = obj.str.replace(foundStr, link);
      res = elPattern.exec(obj.str);
    }

    let ztCount = 0;
    // transform Zieharmonika Text (bib text)
    let ztPattern = /&lt;zt-bib&gt;&lt;show&gt;(.*?)&lt;\/show&gt;&lt;bg&gt;(.*?)&lt;\/bg&gt;&lt;\/zt-bib&gt;/g;
    res = ztPattern.exec(obj.str);
    while (res) {
      // console.log(res);
      ztCount++;
      const foundStr: string = res[0];
      const showContent = res[1];
      const bgContent = res[2];
      const markId = this.hashStrToNum(foundStr + '_ztCount_' + ztCount).toString();
      let newText = '<span id="' + markId + '-show" onClick="clickZTBibMark(' + markId + ')" class="zt-show">' + showContent + '</span>' + '&nbsp;<div id="' + markId + '" onClick="clickZTBibMark(' + markId + ')" class="zt-mark zt-mark-bib-closed"></div>&nbsp;';
      newText += '<span id="' + markId + '-bg" style="display: none;" class="zt-bib-bg">' + bgContent + '</span>';
      obj.str = obj.str.replace(foundStr, newText);
      res = ztPattern.exec(obj.str);
    }

    // transform Zieharmonika Text (info text)
    ztPattern = /&lt;zt-info&gt;&lt;show&gt;(.*?)&lt;\/show&gt;&lt;bg&gt;(.*?)&lt;\/bg&gt;&lt;\/zt-info&gt;/g;
    res = ztPattern.exec(obj.str);
    while (res) {
      // console.log(res);
      ztCount++;
      const foundStr = res[0];
      const showContent = res[1];
      const bgContent = res[2];
      const markId = this.hashStrToNum(foundStr + '_ztCount_' + ztCount).toString();
      let newText = '<span id="' + markId + '-show" onClick="clickZTInfoMark(' + markId + ')" class="zt-show">' + showContent + '</span>' + '&nbsp;<div id="' + markId + '" onClick="clickZTInfoMark(' + markId + ')" class="zt-mark zt-mark-info-closed"></div>&nbsp;';
      newText += '<div id="' + markId + '-bg" style="display: none;" class="zt-info-bg"><span  >' + bgContent + '<br/></span></div>';
      obj.str = obj.str.replace(foundStr, newText);
      res = ztPattern.exec(obj.str);
    }

    // transform FuD internal links
    const internalLinkPattern = /<span class="doc-(\d+)#(.*?) +.*?">(.*?)<\/span>/g;
    res = internalLinkPattern.exec(obj.str);
    while (res) {
      // console.log(res);
      let linkedDocId = -1;
      let link = '';
      const foundStr = res[0];
      if (res[1]) {
        linkedDocId = parseInt(res[1], 10);
      }
      const linkComment = res[2];
      const linkContent = res[3];
      if (linkComment === 'sl') {
        obs = obs.pipe(
          mergeMap(() => {
            return this.getDocumentDataWithFilter([linkedDocId], '56_Slide_Sets');
          }),
          mergeMap((d: any) => {
            if (d.doc && d.doc[0]) {
              const ssInfo = d.doc[0]._source['56_Slide_Sets'];
              if (ssInfo && ssInfo[0]) {
                const ssId = ssInfo[0].slmasssss;
                link = `<a target="_blank" href="#/ae/${ssId}?view=s-objectview&sid=${linkedDocId}&thumbnail=0">${linkContent}</a>`;
                obj.str = obj.str.replace(foundStr, link);
              }
            }
            return of({});
          }),
        );
      } else if (linkComment === 'cp') {
        obs = obs.pipe(
          mergeMap(() => {
          return this.searchLinkedDocument(linkedDocId, 137, 'true');
          }),
          mergeMap((d: any) => {
            if (d.data && d.data[0]) {
              const ceInfo = d.data[0];
              if (ceInfo && ceInfo.linkedDocId) {
                const ceId = ceInfo.linkedDocId;
                link = `<a target="_blank" href="#/ce/${ceId}/subset/${linkedDocId}">${linkContent}</a>`;
                obj.str = obj.str.replace(foundStr, link);
              }
            }
            return of({});
          }),
        );
      } else if (linkComment === 'ss') {
        obs = obs.pipe(
          mergeMap(() => {
            link = `<a target="_blank" href="#/ae/${linkedDocId}">${linkContent}</a>`;
            obj.str = obj.str.replace(foundStr, link);
            return of({});
          }),
        );
      } else if (linkComment === 'ce') {
        obs = obs.pipe(
          mergeMap(() => {
            link = `<a target="_blank" href="#/ce/${linkedDocId}">${linkContent}</a>`;
            obj.str = obj.str.replace(foundStr, link);
            return of({});
          }),
        );
      }
      res = internalLinkPattern.exec(obj.str);
    }
    
    return obs;
  }

    /**
   * get timeline data
   * 
   * @static
   * @param {number[]} docIds 
   * @returns {Observable<any>} 
   * @memberof FudapiService
   */
     public static getTimelineData(): Observable<any> {
      const url: string = this.url + 'timeline';
      const obs = Observable.create((o: Observer<any>) => {
        r(
          {
            url: url,
            method: 'GET',
            headers: this.headerOptions
          },
          (err, res, body) => {
            if (err) {
              o.error(err);
            } else {
              let next = {};
              if (typeof body !== 'object') {
                next = JSON.parse(body);
              } else {
                next = body;
              }
              o.next(next);
            }
            o.complete();
          }
        );
      });
      return obs;
    }


  /**
   * pure function to hash a given str in number
   * 
   * @static
   * @param {string} str 
   * @returns {number} 
   * @memberof FudapiService
   */
  public static hashStrToNum(str: string): number {
    let hash = 5381;
    let i = str.length;
    while (i) {
      hash = (hash * 33) ^ str.charCodeAt(--i);
    }
    return hash >>> 0;
  }
}

/**
 * click on the mark of 'Ziehharmonika Text bib mark'
 * 
 * @param {number} markId 
 */
(<any> window).clickZTBibMark = (markId: number) => {
  const marker = $('#' + markId);
  if (marker.hasClass('zt-mark-bib-closed')) {
    $('#' + markId + '-bg').show('slow');
    marker.removeClass('zt-mark-bib-closed');
    marker.addClass('zt-mark-bib-extended');
  } else if (marker.hasClass('zt-mark-bib-extended')) {
    $('#' + markId + '-bg').hide('slow');
    marker.removeClass('zt-mark-bib-extended');
    marker.addClass('zt-mark-bib-closed');
  }

};

/**
 * click on the mark of 'Ziehharmonika Text info mark'
 * 
 * @param {number} markId 
 */
(<any> window).clickZTInfoMark = (markId: number) => {
  const marker = $('#' + markId);
  if (marker.hasClass('zt-mark-info-closed')) {
    $('#' + markId + '-bg').show('slow');
    marker.removeClass('zt-mark-info-closed');
    marker.addClass('zt-mark-info-extended');
  } else if (marker.hasClass('zt-mark-info-extended')) {
    $('#' + markId + '-bg').hide('slow');
    marker.removeClass('zt-mark-info-extended');
    marker.addClass('zt-mark-info-closed');
  }

};
